import { Inject, Injectable } from '@angular/core';

import * as FullStory from '@fullstory/browser';

import { EnableClassTelemetry } from 'app/services/otel.service';
import {
  FULLSTORY,
  FullStoryOptions,
  FullStorySession,
  FULLSTORY_OPTIONS,
} from '../full-story.module';

@EnableClassTelemetry()
@Injectable()
export class FullStoryService {
  private appConfig = {};
  private sessionConfig: FullStorySession = {};

  constructor(
    @Inject(FULLSTORY) private fullStory: typeof FullStory,
    @Inject(FULLSTORY_OPTIONS) private options: FullStoryOptions
  ) {
    if (!options.recordCrossDomainIFrames) {
      this.appConfig = {
        app: options.appName,
        environment: options.environment,
      };
      this.sessionConfig = {
        environment: options.environment,
        app: options.appName,
      };
      this.event('Session Initialized', {});
      this.fullStory.setUserVars(this.appConfig);
    }
  }

  identify(id: string, identifiers: FullStory.UserVars): void {
    let blacklisted = false;

    if (
      identifiers.email &&
      this.options.blacklistEmails?.includes(identifiers.email.toLocaleLowerCase())
    ) {
      blacklisted = true;
    }
    this.fullStory.identify(id, {
      ...identifiers,
      ...this.appConfig,
      blacklisted: blacklisted || undefined,
    });

    if (blacklisted) {
      this.pause();
      if (this.options.debug || this.options.environment === 'local') {
        console.warn('User blacklisted from FS Logging.  FS has been shut down.');
      }
    }
  }

  setSessionData(sessionData: FullStorySession): void {
    this.sessionConfig = {
      ...this.sessionConfig,
      ...sessionData,
    };
    this.event('Session Updated', {});
  }

  setPageVars(pageName: string, vars?: { [key: string]: any }) {
    const properties = {
      ...vars,
      ...{ pageName },
    };
    this.fullStory.setVars('page', properties);
  }

  setUserVars(vars: FullStory.UserVars): void {
    this.fullStory.setUserVars(vars);
  }

  // tslint:disable-next-line: no-any
  event(eventName: string, eventData: any): void {
    this.fullStory.event(eventName, { ...this.sessionConfig, ...eventData });
  }

  anonymize(): void {
    this.fullStory.anonymize();
  }

  log(level: FullStory.LogLevel, message: string): void {
    this.fullStory.log(level, message);
  }

  consent(hasConsent: boolean): void {
    this.fullStory.consent(hasConsent);
  }

  pause(): void {
    this.fullStory.shutdown();
  }

  start(): void {
    this.fullStory.restart();
  }
}

export enum PageNames {
  dashboard = 'Dashbard View',
  project = 'Project View',
  upload_file = 'Upload File',
  upload_mapping = 'Upload Mapping',
  configurations = 'Configurations List',
  config_detail = 'Config Detail', // TBD
  config_route = 'Config Route View', // TBD
  groups = 'Groups', // TBD
  users = 'Users', // TBD
}

export enum PrivacyClasses {
  exclude = 'fs-exclude',
  mask = 'fs-mask',
  unmask = 'fs-unmask',
  excludeWithoutConsent = 'fs-exclude-without-consent',
  maskWithoutConsent = 'fs-mask-without-consent',
  unmaskWithoutConsent = 'fs-unmask-with-consent',
}
